import { Box, Text, Flex, Button, Skeleton } from '@chakra-ui/react';
import { useCallback, useContext, useMemo } from 'react';
import { useHasLoaded } from 'hooks/useHasLoaded';
import { GlobalContext } from '../../providers/Global';
import { MobileMenu } from './components/MobileMenu';
import { useParams } from 'react-router-dom';
import { ApplicationRoutes } from 'utils/routes';
import { ModalType } from 'utils/constants';
import { ReactComponent as HederaLogo } from 'assets/hedera-logo.svg';
import { ReactComponent as PlusIcon } from 'assets/plus-icon.svg';
import TokenIcon from 'components/TokenIcon';
import { formatTokenBalance } from 'utils/tokens';
import AnimatedNumber from 'components/AnimatedNumber';

const AssetsPage = () => {
  const { walletId, multisigAccountId } = useParams();
  const { currentAccount, openModal, setSelectedToken } = useContext(GlobalContext);

  const tokens = useMemo(() => currentAccount.tokens || [], [currentAccount.tokens]);

  const balanceCanAnimate = useHasLoaded(currentAccount.balances);

  const handleAddToken = useCallback(() => {
    openModal(ModalType.CreateTransactionWizard, { presetType: 'TOKEN_ASSOCIATE' });
  }, [openModal]);

  return (
    <Flex
      flex={1}
      flexDirection="column"
    >
      <MobileMenu
        pageName='Assets'
        backUrl={`${ApplicationRoutes.Dashboard}/${walletId}/account/${multisigAccountId}`}
      />
      <Flex
        p={6}
        bg="white"
        height="100%"
        borderRadius={16}
        flexDirection="column"
        boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
      >
        <Flex width="full" alignItems="center" h={10} borderRadius={6} p={4} bg="primary">
          <Box flex={1}>
            <Text size="xs">Asset</Text>
          </Box>
          <Box>
            <Text size="xs">Amount</Text>
          </Box>
        </Flex>
        <Box display="flex" width="full" flexDirection="column">
          {/* HBAR row */}
          <Flex
            h={14}
            p={4}
            width="full"
            borderRadius={8}
            alignItems="center"
            borderBottom="1px solid #F6F6F6"
          >
            <Flex flex={1} alignItems="center" gap={3}>
              <HederaLogo width="28px" height="28px" />
              <Box>
                <Text size="xs" fontWeight="600">HBAR</Text>
                <Text fontSize="11px" color="blackAlpha.500">Hedera</Text>
              </Box>
            </Flex>
            <Box textAlign="right">
              <Skeleton isLoaded={!!currentAccount.balances} borderRadius={6} minH="16px">
                <AnimatedNumber
                  value={Number(currentAccount.balances?.hbars?.replace(/[^0-9.]/g, '') || '0')}
                  animate={balanceCanAnimate}
                  size="xs"
                />
              </Skeleton>
            </Box>
          </Flex>

          {/* HTS Token rows */}
          {tokens.map((token) => (
            <Flex
              key={token.token_id}
              h={14}
              p={4}
              width="full"
              borderRadius={8}
              alignItems="center"
              borderBottom="1px solid #F6F6F6"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              transition="background 0.15s"
              onClick={() => {
                setSelectedToken(token);
                openModal(ModalType.TokenDetailsModal);
              }}
            >
              <Flex flex={1} alignItems="center" gap={3}>
                <TokenIcon symbol={token.symbol} size={28} tokenId={token.token_id} />
                <Box>
                  <Text size="xs" fontWeight="600">{token.symbol}</Text>
                  <Text fontSize="11px" color="blackAlpha.500">{token.name}</Text>
                </Box>
              </Flex>
              <Box textAlign="right">
                <Text size="xs">
                  {formatTokenBalance(token)}
                </Text>
              </Box>
            </Flex>
          ))}
        </Box>

        {/* Add token button */}
        <Flex mt={4} justifyContent="center">
          <Button
            variant="outline"
            size="sm"
            borderRadius={8}
            onClick={handleAddToken}
            leftIcon={<PlusIcon style={{ width: 14, height: 14 }} />}
          >
            Add token
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AssetsPage;
